import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { 
  ChevronDown,
  ChevronUp,
  Search,
  MessageCircle,
  ArrowRight
} from 'lucide-react';

const faqCategories = [
  {
    name: 'General',
    faqs: [
      {
        question: 'What is Cost Genie?',
        answer: 'Cost Genie is an AI-powered Quote-to-SOP platform designed specifically for manufacturing suppliers. It digitizes the entire RFQ workflow from receiving customer inquiries to generating production-ready SOPs, replacing manual spreadsheet-based processes with one connected digital platform.',
      },
      {
        question: 'Who is Cost Genie designed for?',
        answer: 'Cost Genie is built for manufacturing companies including auto-component manufacturers (Tier 1, 2, 3), fabrication and sheet metal companies, plastic molding and rubber parts manufacturers, tool & die makers, and engineering & EPC companies.',
      },
      {
        question: 'How is Cost Genie different from using Excel or Google Sheets?',
        answer: 'Unlike spreadsheets, Cost Genie offers automatic version control, built-in approval workflows, real-time collaboration, complete audit trails, automated costing with norms libraries, and management dashboards. It reduces quotation time by 50-70% and eliminates manual errors.',
      },
    ],
  },
  {
    name: 'Features',
    faqs: [
      {
        question: 'What modules are included in the platform?',
        answer: 'Cost Genie includes RFQ Capture & Tracking, BOM Management, Smart Costing Engine, Customer Quote Generation, Vendor Quote Management, APQP Governance, Workflow & Approvals, Audit Trail, and Analytics Dashboards. Add-on modules include Vendor Quote, APQP, and AI BOM Automation.',
      },
      {
        question: 'Does Cost Genie integrate with ERP systems?',
        answer: 'Yes, Cost Genie offers pre-built integrations with SAP, Oracle, Microsoft Dynamics, and Tally. We also provide a comprehensive REST API for custom integrations with any system.',
      },
      {
        question: 'What AI capabilities does Cost Genie offer?',
        answer: 'Our AI features include automated BOM extraction from drawings, intelligent document OCR, meeting minutes analysis, predictive analytics for win rates, risk flagging, and natural language query dashboards. The AI BOM Automation module is available as an add-on.',
      },
    ],
  },
  {
    name: 'Pricing',
    faqs: [
      {
        question: 'How much does Cost Genie cost?',
        answer: 'We offer three plans: Essentials at $25/month (1 user, 250 RFQs/year), Professional at $100/month (5 users, 1,000 RFQs/year), and Enterprise with custom pricing. Annual billing saves 20%. Additional modules like Vendor Costing and AI Copilot Suite are available as add-ons.',
      },
      {
        question: 'Is there a free trial?',
        answer: 'Yes, we offer a 30-day free trial with full access to Professional features. No credit card required. At the end of the trial, you can choose the plan that fits your needs.',
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards (Visa, MasterCard, American Express) through our secure Stripe payment processing. Enterprise customers can arrange bank transfers.',
      },
    ],
  },
  {
    name: 'Implementation',
    faqs: [
      {
        question: 'How long does implementation take?',
        answer: 'Implementation typically takes 2-4 weeks depending on complexity. This includes system configuration, data migration, user training, and go-live support. Enterprise implementations may take longer based on custom requirements.',
      },
      {
        question: 'Do you provide training?',
        answer: 'Yes, all plans include onboarding training. Professional and Enterprise plans include more comprehensive training sessions. We also provide documentation, video tutorials, and ongoing support.',
      },
      {
        question: 'What support options are available?',
        answer: 'Essentials includes email support, Professional includes priority support with faster response times, and Enterprise includes 24/7 premium support with a dedicated account manager.',
      },
    ],
  },
  {
    name: 'Security',
    faqs: [
      {
        question: 'Is my data secure?',
        answer: 'Yes, Cost Genie uses enterprise-grade security including AES-256 encryption for data at rest and in transit, role-based access control, regular security audits, and SOC 2 compliance readiness. Your data is hosted in secure, redundant data centers.',
      },
      {
        question: 'Do you offer SSO/SAML integration?',
        answer: 'Yes, SSO/SAML integration is available for Enterprise customers. This allows your team to use their existing corporate credentials to access Cost Genie.',
      },
      {
        question: 'What happens to my data if I cancel?',
        answer: 'Your data remains accessible for 30 days after cancellation. You can export all your data at any time in standard formats. After 30 days, data is securely deleted per our retention policy.',
      },
    ],
  },
];

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('General');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaqs = searchQuery
    ? faqCategories.flatMap(cat => 
        cat.faqs.filter(faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : faqCategories.find(cat => cat.name === activeCategory)?.faqs || [];

  return (
    <div className="pt-24" data-testid="faq-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-white/60 text-lg mb-10">
              Find answers to common questions about Cost Genie
            </p>

            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-full text-white placeholder:text-white/30 focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF] outline-none transition-all"
                data-testid="faq-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Tabs */}
      {!searchQuery && (
        <section className="pb-8">
          <div className="max-w-4xl mx-auto px-6">
            <AnimatedSection>
              <div className="flex flex-wrap justify-center gap-3">
                {faqCategories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => {
                      setActiveCategory(category.name);
                      setExpandedFaq(null);
                    }}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                      activeCategory === category.name
                        ? 'bg-[#00D4FF] text-black'
                        : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid={`faq-category-${category.name.toLowerCase()}`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* FAQ List */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatedSection>
            {searchQuery && filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-white/60">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <div
                    key={index}
                    className="glass-card overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-6 text-left"
                      data-testid={`faq-item-${index}`}
                    >
                      <span className="font-medium pr-4">{faq.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-[#00D4FF] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                      )}
                    </button>
                    <motion.div
                      initial={false}
                      animate={{ height: expandedFaq === index ? 'auto' : 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-white/60 leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <div className="glass-card p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-[#00D4FF]/10 rounded-full blur-[100px]" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-white/60 mb-8 max-w-xl mx-auto">
                  Our team is ready to help. Reach out and we'll get back to you within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/demo"
                    className="px-8 py-4 bg-[#00D4FF] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all flex items-center gap-2"
                    data-testid="faq-contact-btn"
                  >
                    Contact Us
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <a
                    href="https://wa.me/+918055082222"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-8 py-4 text-white font-medium rounded-full border border-white/20 hover:bg-white/5 transition-all flex items-center gap-2"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default FAQPage;
